/**
 * Sign-off for a stored deliverable (CLARID-SPEC-AGENT-001 §1A.9): preparer → reviewer → approver on the
 * same file. The server checks each person's permission, keeps the preparer from reviewing or approving
 * their own work, re-checks the file's fingerprint and records each signature before it counts.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  PencilSquareIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { agentApi, type DeliverableCardState, type SignoffRole } from '../../lib/agent';
import { primaryBtn, secondaryBtn } from './cardKit';

const STEPS: { role: SignoffRole; label: string; done: string }[] = [
  { role: 'PREPARER', label: 'Prepared', done: 'Sign as preparer' },
  { role: 'REVIEWER', label: 'Reviewed', done: 'Sign as reviewer' },
  { role: 'APPROVER', label: 'Approved', done: 'Approve (final)' },
];

const NEXT: Partial<Record<DeliverableCardState['status'], SignoffRole>> = {
  DRAFT: 'PREPARER',
  PREPARED: 'REVIEWER',
  REVIEWED: 'APPROVER',
};

const STATUS: Record<DeliverableCardState['status'], { label: string; cls: string }> = {
  DRAFT: { label: 'Draft', cls: 'bg-gray-100 text-gray-700' },
  PREPARED: { label: 'Awaiting review', cls: 'bg-amber-50 text-amber-800' },
  REVIEWED: { label: 'Awaiting approval', cls: 'bg-amber-50 text-amber-800' },
  APPROVED: { label: 'Final', cls: 'bg-emerald-50 text-emerald-800' },
  RETURNED: { label: 'Returned for changes', cls: 'bg-red-50 text-red-700' },
};

const day = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function DeliverableSignoff({ teamId, deliverableId }: { teamId: string; deliverableId: string }) {
  const qc = useQueryClient();
  const key = ['agent-deliverable', teamId, deliverableId];
  const { data: card } = useQuery({ queryKey: key, queryFn: () => agentApi.deliverable(teamId, deliverableId).then((r) => r.data.card) });
  const [note, setNote] = useState('');
  const [returning, setReturning] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  // Errors (permission, order, the preparer signing twice) are shown by the API layer.
  const sign = useMutation({ mutationFn: (role: SignoffRole) => agentApi.signDeliverable(teamId, deliverableId, role, note.trim() || undefined), onSuccess: () => { setNote(''); refresh(); } });
  const sendBack = useMutation({ mutationFn: () => agentApi.returnDeliverable(teamId, deliverableId, note.trim()), onSuccess: () => { setNote(''); setReturning(false); refresh(); } });
  if (!card) return null;

  const next = NEXT[card.status];
  const signed = (role: SignoffRole) => card.signoffs.find((s) => s.role === role);
  const returned = card.signoffs.find((s) => s.role === 'RETURNED');
  const busy = sign.isPending || sendBack.isPending;
  const status = STATUS[card.status];

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <DocumentCheckIcon className="h-4 w-4 text-ocean-600" />
        <span className="font-medium text-navy-900">{card.fileName}</span>
        <span className={clsx('rounded-full px-2 py-0.5 font-medium', status.cls)}>{status.label}</span>
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1 font-medium text-ocean-700 hover:text-ocean-800"
          onClick={() => agentApi.download(card.downloadPath, card.fileName).catch(() => undefined)}
        >
          <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Download
        </button>
      </div>

      <ol className="mt-2 grid gap-1 sm:grid-cols-3">
        {STEPS.map((step) => {
          const s = signed(step.role);
          return (
            <li key={step.role} className={clsx('flex items-start gap-1.5', s ? 'text-emerald-700' : 'text-gray-400')}>
              <CheckCircleIcon className="mt-px h-4 w-4 shrink-0" />
              <span>
                {step.label}
                {s && <span className="block text-gray-600">{s.userName}, {day(s.signedAt)}</span>}
              </span>
            </li>
          );
        })}
      </ol>
      {returned && (
        <p className="mt-2 text-red-700">
          Returned by {returned.userName} on {day(returned.signedAt)}: “{returned.note}”. Produce a corrected version to sign.
        </p>
      )}

      {next && (
        <div className="mt-2 space-y-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 2000))}
            placeholder={returning ? 'What needs to change? (required)' : 'Note for the record (optional)'}
            className="w-full rounded-md border border-gray-300 px-2 py-1 focus:border-ocean-500 focus:outline-none focus:ring-1 focus:ring-ocean-500"
          />
          <div className="flex flex-wrap gap-2">
            {!returning && (
              <button type="button" className={primaryBtn} disabled={busy} onClick={() => sign.mutate(next)}>
                <PencilSquareIcon className="h-4 w-4" /> {STEPS.find((s) => s.role === next)!.done}
              </button>
            )}
            {next !== 'PREPARER' && (
              returning ? (
                <>
                  <button type="button" className={primaryBtn} disabled={busy || !note.trim()} onClick={() => sendBack.mutate()}>
                    <ArrowUturnLeftIcon className="h-4 w-4" /> Return for changes
                  </button>
                  <button type="button" className={secondaryBtn} disabled={busy} onClick={() => setReturning(false)}>Cancel</button>
                </>
              ) : (
                <button type="button" className={secondaryBtn} disabled={busy} onClick={() => setReturning(true)}>
                  <ArrowUturnLeftIcon className="h-4 w-4" /> Return for changes
                </button>
              )
            )}
          </div>
          <p className="text-gray-400">
            {next === 'PREPARER'
              ? 'Download and read it first. Signing records that you prepared this exact file.'
              : 'The preparer cannot review or approve their own work. Your signature is recorded against this exact file.'}
          </p>
        </div>
      )}
    </div>
  );
}
